// Pure pieces of the signup flow: where a visit to /signup starts (from the URL and the saved
// session), which step a screen belongs to, how a Stripe confirmation or an /activate error is
// handled, and the exact trial disclosure. No React, no I/O (except the sessionStorage helpers).
// Contract: packages/shared/src/signup.ts. Flow: docs/TRIALS.md.

import { isPlanTier, type PlanTier } from "@gf/shared";
import { ApiError } from "../../../lib/api";
import { formatLongDate, formatUsd } from "../../../lib/billing";

export const DEFAULT_PLAN: PlanTier = "growth";

/** Where Stripe sends the browser back after a redirect-based check (3-D Secure in a new page). */
export const CARD_RETURN_PATH = "/signup?card=return";

// ── Entry: what the URL asks for ────────────────────────────────────────────

export type SignupEntry =
  /** #upgrade=… : a free-trial account's paid offer (UpgradeOffer). */
  | { kind: "upgrade"; token: string }
  /** #token=… : the link from the verification email. */
  | { kind: "email"; token: string }
  /** ?card=return&payment_intent=…&redirect_status=… : back from Stripe's redirect. */
  | { kind: "card_return"; session: string | null; failed: boolean }
  /** #session=… (a pending signup that signed in at /login), or the session saved in this tab. */
  | { kind: "resume"; session: string; fromHash: boolean }
  /** ?plan=…&code=… : a fresh signup (code: a discount code, e.g. from the gramfather.com exit offer text). */
  | { kind: "start"; plan: PlanTier; code: string };

/** redirect_status values that mean the bank said no (anything else: ask the API). */
const FAILED_REDIRECTS = new Set(["failed", "requires_payment_method", "canceled"]);

/** "?code=GF25-AB12CD" → the code as typed (letters, digits, dash, underscore; up to 40), else "". */
export function codeFromQuery(value: string | null | undefined): string {
  const v = (value ?? "").trim();
  return /^[A-Za-z0-9_-]{1,40}$/.test(v) ? v.toUpperCase() : "";
}

/** "?plan=pro" → "pro"; anything else → Growth. */
export function planFromQuery(value: string | null | undefined): PlanTier {
  const v = (value ?? "").trim().toLowerCase();
  return isPlanTier(v) ? v : DEFAULT_PLAN;
}

export function signupEntry(loc: { search: string; hash: string }, storedSession: string | null): SignupEntry {
  const hash = new URLSearchParams(loc.hash.replace(/^#/, ""));
  const query = new URLSearchParams(loc.search);
  const upgrade = hash.get("upgrade");
  if (upgrade) return { kind: "upgrade", token: upgrade };
  const token = hash.get("token");
  if (token) return { kind: "email", token };
  const hashSession = hash.get("session");
  if (query.get("card") === "return") {
    return { kind: "card_return", session: hashSession || storedSession || null, failed: FAILED_REDIRECTS.has(query.get("redirect_status") ?? "") };
  }
  if (hashSession) return { kind: "resume", session: hashSession, fromHash: true };
  if (storedSession) return { kind: "resume", session: storedSession, fromHash: false };
  return { kind: "start", plan: planFromQuery(query.get("plan")), code: codeFromQuery(query.get("code")) };
}

// ── Steps ───────────────────────────────────────────────────────────────────

export type SignupView = "details" | "check_email" | "recover" | "password" | "card" | "activating";

export const SIGNUP_STEPS = ["Details", "Verify email", "Password", "Card"] as const;

/** 1-based step for the indicator. */
export function stepFor(view: SignupView): 1 | 2 | 3 | 4 {
  switch (view) {
    case "details":
      return 1;
    case "check_email":
    case "recover":
      return 2;
    case "password":
      return 3;
    case "card":
    case "activating":
      return 4;
  }
}

// ── Card confirmation + activation ──────────────────────────────────────────

/** What to do with the PaymentIntent status Stripe.js hands back after confirmPayment. */
export type ConfirmOutcome = "activate" | "processing" | "declined" | "incomplete";

export function confirmOutcome(status: string | null | undefined): ConfirmOutcome {
  switch (status) {
    case "requires_payment_method":
    case "canceled":
      return "declined";
    case "processing":
      return "processing";
    case "requires_action":
    case "requires_confirmation":
      return "incomplete";
    default:
      // requires_capture is the expected one (the hold is in place). Anything else: the API checks the hold.
      return "activate";
  }
}

/**
 * How an error from POST /api/auth/signup/* is handled: start over from a fresh email link (expired),
 * try another card (card), sign in because the account exists (account), or try the same thing again.
 */
export type SignupFailure = "expired" | "card" | "account" | "retry";

export function signupFailure(err: unknown): SignupFailure {
  if (!(err instanceof ApiError)) return "retry";
  if (err.code === "email_taken" || err.code === "already_registered") return "account";
  if (err.code === "signup_session_expired" || err.status === 401) return "expired";
  if (err.code === "card_not_confirmed" || err.code === "trial_card_used" || err.status === 402) return "card";
  return "retry";
}

export function errorText(err: unknown, fallback = "Something went wrong. Try again."): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

// ── The disclosure ──────────────────────────────────────────────────────────

export interface TrialTerms {
  trialDays: number;
  /** The temporary hold, in cents (the plan's first month). */
  holdCents: number;
  /** The monthly price after the trial, in cents. */
  priceCents: number;
  /** When the first charge happens if they don't cancel (epoch ms). */
  trialEndsAt: number;
  /** A discount code's label ("25% off for life"); priceCents is already the discounted price. */
  discountLabel?: string | null;
}

export interface TrialDisclosure {
  lead: string;
  hold: string;
  cancel: string;
  /** "Payments are non-refundable." */
  refunds: string;
  /** All four sentences. */
  text: string;
}

/**
 * "Free for 14 days, then $49/month. We place a temporary $49 hold to confirm your card and release
 * it right away. Cancel any time before October 25, 2026 and you won't be charged."
 */
export function trialDisclosure(t: TrialTerms): TrialDisclosure {
  const lead = `Free for ${t.trialDays} days, then ${formatUsd(t.priceCents)}/month${t.discountLabel ? ` (${t.discountLabel})` : ""}.`;
  const hold = `We place a temporary ${formatUsd(t.holdCents)} hold to confirm your card and release it right away.`;
  const cancel = `Cancel any time before ${formatLongDate(t.trialEndsAt)} and you won't be charged.`;
  const refunds = "Payments are non-refundable.";
  return { lead, hold, cancel, refunds, text: `${lead} ${hold} ${cancel} ${refunds}` };
}

export function trialButtonLabel(trialDays: number): string {
  return `Start my ${trialDays}-day free trial`;
}

// ── The signup session (this tab only) ──────────────────────────────────────

const SESSION_KEY = "gf_signup_session";

export function readStoredSession(): string | null {
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

export function storeSession(session: string): void {
  try {
    sessionStorage.setItem(SESSION_KEY, session);
  } catch {
    /* Storage blocked: the signup still works in this page view. */
  }
}

export function clearStoredSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* nothing to clear */
  }
}
